import { useEffect, useState } from 'react';
import { fetchAssessmentNextStep, NEXT_STEP_STATUS } from 'services/apis/assessmentNextStep';

export const NEXT_STEP_CHECKING = 'checking';
// Still pending after DELAYED_AFTER_MS: tell the candidate they can leave, keep polling.
export const NEXT_STEP_DELAYED = 'delayed';
// Still pending at MAX_WAIT_MS (slow result or a failing server): polling has stopped.
export const NEXT_STEP_UNRESOLVED = 'unresolved';

const POLL_INTERVAL_MS = 5000;
const DELAYED_AFTER_MS = 7 * 60 * 1000;
const MAX_WAIT_MS = 10 * 60 * 1000;

/**
 * Polls whether the candidate goes straight from this assessment into the AI interview.
 * `checking` until the first answer, `pending` while the result is not final, `delayed`
 * once that has gone on for DELAYED_AFTER_MS, then settles on `ready` (with interviewUrl),
 * `none`, or `unresolved` when MAX_WAIT_MS passes without a final answer.
 * @param {string} linkId
 * @param {boolean} enabled decided once on arrival; when false the answer is `none`
 */
export const useAssessmentNextStep = ({ linkId, enabled }) => {
  const [step, setStep] = useState({
    status: enabled ? NEXT_STEP_CHECKING : NEXT_STEP_STATUS.NONE,
  });

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    let timer = null;
    const startedAt = Date.now();
    const deadline = startedAt + MAX_WAIT_MS;

    const poll = async () => {
      const result = await fetchAssessmentNextStep(linkId);
      if (cancelled) return;
      if (result.status !== NEXT_STEP_STATUS.PENDING) {
        setStep(result);
        return;
      }
      if (Date.now() >= deadline) {
        setStep({ status: NEXT_STEP_UNRESOLVED });
        return;
      }
      setStep({
        status: Date.now() - startedAt >= DELAYED_AFTER_MS ? NEXT_STEP_DELAYED : NEXT_STEP_STATUS.PENDING,
      });
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [enabled, linkId]);

  return step;
};
